import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Upload, Edit, Save, X, Plus, Trash2, Users, CheckCircle, XCircle, Search, ChevronRight, GripVertical, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Simple formatting toolbar component
const FormatToolbar = ({ editorRef }) => {
  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };

  return (
    <div className="flex items-center gap-1 p-2 border-b bg-gray-50 rounded-t-lg" data-testid="format-toolbar">
      <button type="button" onClick={() => exec('bold')} className="px-2 py-1 rounded hover:bg-gray-200 font-bold text-sm" title="Bold">B</button>
      <button type="button" onClick={() => exec('italic')} className="px-2 py-1 rounded hover:bg-gray-200 italic text-sm" title="Italic">I</button>
      <button type="button" onClick={() => exec('underline')} className="px-2 py-1 rounded hover:bg-gray-200 underline text-sm" title="Underline">U</button>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      <button type="button" onClick={() => exec('insertUnorderedList')} className="px-2 py-1 rounded hover:bg-gray-200 text-sm" title="Bullet List">&#8226; List</button>
      <button type="button" onClick={() => exec('insertOrderedList')} className="px-2 py-1 rounded hover:bg-gray-200 text-sm" title="Numbered List">1. List</button>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      <button type="button" onClick={() => exec('formatBlock', 'h3')} className="px-2 py-1 rounded hover:bg-gray-200 text-sm font-semibold" title="Sub-heading">H3</button>
      <button type="button" onClick={() => exec('formatBlock', 'p')} className="px-2 py-1 rounded hover:bg-gray-200 text-sm" title="Paragraph">P</button>
    </div>
  );
};

const HandbookManager = () => {
  const [sections, setSections] = useState([]);
  const [meta, setMeta] = useState(null);
  const [readStats, setReadStats] = useState({ total_reads: 0, total_employees: 0 });
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [activeSection, setActiveSection] = useState(null);
  const [search, setSearch] = useState('');
  const [importing, setImporting] = useState(false);
  const [readData, setReadData] = useState(null);
  const [readDialogOpen, setReadDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const sectionRefs = useRef({});

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => { loadSections(); }, []);

  const loadSections = async () => {
    try {
      const res = await axios.get(`${API}/api/handbook-full/sections`);
      setSections(res.data.sections || []);
      setMeta(res.data.meta);
      setReadStats(res.data.read_stats || { total_reads: 0, total_employees: 0 });
      if (res.data.sections?.length > 0 && !activeSection) {
        setActiveSection(res.data.sections[0].id);
      }
    } catch (err) {
      console.error('Failed to load handbook sections:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.docx')) {
      toast({ title: 'Error', description: 'Only .docx files are supported', variant: 'destructive' });
      return;
    }

    if (!window.confirm('This will replace all existing handbook sections. Continue?')) {
      e.target.value = '';
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/api/handbook-full/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'Import Successful', description: res.data.message });
      loadSections();
    } catch (err) {
      toast({ title: 'Import Failed', description: err.response?.data?.detail || 'Failed to import', variant: 'destructive' });
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const startEdit = (section) => {
    setEditingSection(section.id);
    setEditTitle(section.title);
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = section.content || '';
        editorRef.current.focus();
      }
    }, 50);
  };

  const saveEdit = async () => {
    if (!editingSection) return;
    try {
      const content = editorRef.current?.innerHTML || '';
      await axios.put(`${API}/api/handbook-full/sections/${editingSection}`, {
        title: editTitle,
        content: content,
      });
      toast({ title: 'Saved', description: 'Section updated successfully' });
      setEditingSection(null);
      loadSections();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save section', variant: 'destructive' });
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
  };

  const deleteSection = async (sectionId) => {
    if (!window.confirm('Delete this section permanently?')) return;
    try {
      await axios.delete(`${API}/api/handbook-full/sections/${sectionId}`);
      toast({ title: 'Deleted', description: 'Section removed' });
      loadSections();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const addSection = async () => {
    if (!newTitle.trim()) return;
    try {
      await axios.post(`${API}/api/handbook-full/sections`, {
        title: newTitle,
        content: newContent || '<p>Enter content here...</p>',
      });
      toast({ title: 'Added', description: 'New section created' });
      setAddDialogOpen(false);
      setNewTitle('');
      setNewContent('');
      loadSections();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to add section', variant: 'destructive' });
    }
  };

  const loadReadStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/handbook-full/reads`);
      setReadData(res.data);
      setReadDialogOpen(true);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load read status', variant: 'destructive' });
    }
  };

  const resetReadStatus = async (employeeId) => {
    try {
      await axios.delete(`${API}/api/handbook-full/reads/${employeeId}`);
      toast({ title: 'Reset', description: 'Employee read status reset to unread' });
      loadReadStatus();
      loadSections();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to reset read status', variant: 'destructive' });
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filtered = search
    ? sections.filter(s => s.title.toLowerCase().includes(search.toLowerCase()))
    : sections;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="handbook-manager">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-indigo-600" />
            Employee Handbook
          </h1>
          <p className="text-gray-500 mt-1">
            {sections.length} sections
            {meta?.last_imported && ` · Last imported ${new Date(meta.last_imported).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={loadReadStatus} data-testid="view-reads-btn">
            <Users className="h-4 w-4 mr-2" />
            Read Status ({readStats.total_reads}/{readStats.total_employees})
          </Button>
          <Button
            variant="outline"
            onClick={() => { window.location.href = `${API}/api/handbook-full/download-pdf`; }}
            data-testid="download-handbook-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PDF
          </Button>
          {isSuperAdmin && (
            <>
              <Button variant="outline" onClick={() => setAddDialogOpen(true)} data-testid="add-section-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Section
              </Button>
              <Button onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="import-docx-btn">
                <Upload className="h-4 w-4 mr-2" />
                {importing ? 'Importing...' : 'Import DOCX'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                className="hidden"
                onChange={handleImport}
              />
            </>
          )}
        </div>
      </div>

      {/* Main Content: TOC Sidebar + Content */}
      <div className="flex gap-4" style={{ height: 'calc(100vh - 180px)' }}>
        {/* TOC Sidebar */}
        <Card className="w-80 flex-shrink-0 overflow-hidden flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search sections..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="handbook-search"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 overflow-y-auto">
            <nav className="py-1">
              {filtered.map(s => (
                <button
                  key={s.id}
                  onClick={() => scrollToSection(s.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors hover:bg-indigo-50 border-l-2 ${
                    activeSection === s.id
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 font-medium'
                      : 'border-transparent text-gray-600'
                  }`}
                  data-testid={`toc-item-${s.section_number}`}
                >
                  <span className="text-xs text-gray-400 w-5 flex-shrink-0">{s.section_number}.</span>
                  <span className="truncate">{s.title}</span>
                </button>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Content Area */}
        <Card className="flex-1 overflow-hidden flex flex-col">
          <CardContent className="p-0 flex-1 overflow-y-auto">
            <div className="p-6 space-y-8">
              {filtered.map(section => (
                <div
                  key={section.id}
                  ref={el => sectionRefs.current[section.id] = el}
                  className="scroll-mt-4"
                  data-testid={`section-${section.section_number}`}
                >
                  {editingSection === section.id ? (
                    /* Edit Mode */
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-amber-50 border-b flex items-center gap-3">
                        <Input
                          value={editTitle}
                          onChange={e => setEditTitle(e.target.value)}
                          className="flex-1 font-semibold text-lg"
                          data-testid="edit-section-title"
                        />
                        <Button size="sm" onClick={saveEdit} data-testid="save-section-btn">
                          <Save className="h-4 w-4 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <FormatToolbar editorRef={editorRef} />
                      <div
                        ref={editorRef}
                        contentEditable
                        className="p-4 min-h-[200px] focus:outline-none prose prose-sm max-w-none
                          [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5
                          [&_li]:mb-1 [&_strong]:font-bold [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-2
                          [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:px-3 [&_th]:py-2 [&_th]:bg-gray-100
                          [&_td]:border [&_td]:px-3 [&_td]:py-2"
                        data-testid="edit-section-content"
                      />
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="group">
                      <div className="flex items-start justify-between mb-3">
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <span className="text-indigo-500 text-base font-normal">{section.section_number}.</span>
                          {section.title}
                        </h2>
                        {isSuperAdmin && (
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button size="sm" variant="ghost" onClick={() => startEdit(section)} data-testid={`edit-btn-${section.section_number}`}>
                              <Edit className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteSection(section.id)} data-testid={`delete-btn-${section.section_number}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div
                        className="prose prose-sm max-w-none text-gray-700 leading-relaxed
                          [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5
                          [&_li]:mb-1 [&_strong]:font-bold [&_em]:italic
                          [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2
                          [&_.handbook-table]:w-full [&_.handbook-table]:border-collapse [&_.handbook-table]:mt-3
                          [&_.handbook-table_th]:border [&_.handbook-table_th]:px-3 [&_.handbook-table_th]:py-2 [&_.handbook-table_th]:bg-indigo-50 [&_.handbook-table_th]:text-left [&_.handbook-table_th]:text-sm [&_.handbook-table_th]:font-semibold
                          [&_.handbook-table_td]:border [&_.handbook-table_td]:px-3 [&_.handbook-table_td]:py-2 [&_.handbook-table_td]:text-sm"
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                      <hr className="mt-6 border-gray-200" />
                    </div>
                  )}
                </div>
              ))}

              {sections.length === 0 && (
                <div className="text-center py-16">
                  <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                  <h3 className="text-lg font-medium text-gray-500 mb-2">No Handbook Content</h3>
                  <p className="text-sm text-gray-400 mb-4">Import a DOCX file to get started</p>
                  {isSuperAdmin && (
                    <Button onClick={() => fileInputRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" /> Import DOCX
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Read Status Dialog */}
      <Dialog open={readDialogOpen} onOpenChange={setReadDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Handbook Read Status
            </DialogTitle>
          </DialogHeader>
          {readData && (
            <Tabs defaultValue="not-read" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="w-full">
                <TabsTrigger value="not-read" className="flex-1" data-testid="not-read-tab">
                  Not Read ({readData.total_not_read})
                </TabsTrigger>
                <TabsTrigger value="read" className="flex-1" data-testid="read-tab">
                  Read ({readData.total_read})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="not-read" className="flex-1 overflow-y-auto mt-2">
                <div className="space-y-1">
                  {readData.not_read?.map(emp => (
                    <div key={emp.employee_id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                      <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{emp.employee_name}</p>
                        <p className="text-xs text-gray-400">{emp.employee_code} · {emp.department}</p>
                      </div>
                    </div>
                  ))}
                  {readData.not_read?.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-8">All employees have read the handbook!</p>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="read" className="flex-1 overflow-y-auto mt-2">
                <div className="space-y-1">
                  {readData.read?.map(emp => (
                    <div key={emp.employee_id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{emp.employee_name}</p>
                        <p className="text-xs text-gray-400">{emp.employee_code} · {emp.department}</p>
                      </div>
                      <span className="text-xs text-gray-400 mr-2">
                        {emp.read_at && new Date(emp.read_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                        onClick={() => resetReadStatus(emp.employee_id)}
                        data-testid={`reset-read-${emp.employee_code}`}
                      >
                        Mark Unread
                      </Button>
                    </div>
                  ))}
                  {readData.read?.length === 0 && (
                    <p className="text-center text-sm text-gray-400 py-8">No employees have read the handbook yet</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Section Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Section Title</label>
              <Input
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                placeholder="e.g., Remote Work Policy"
                data-testid="new-section-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Initial Content (optional)</label>
              <textarea
                value={newContent}
                onChange={e => setNewContent(e.target.value)}
                className="w-full mt-1 p-3 border rounded-md text-sm min-h-[100px]"
                placeholder="Enter section content or leave blank to edit later..."
                data-testid="new-section-content"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={addSection} disabled={!newTitle.trim()} data-testid="save-new-section-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Section
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HandbookManager;
